import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { COMMON_IMPORTS } from '../../common-imports';
import { AvLanguageDropdownComponent } from '../av-language-dropdown/av-language-dropdown.component';
import { AvAvatarComponent } from '../av-avatar/av-avatar.component';
import { AuthService } from '../../Services/auth.service';
import { TranslationService } from '../../Services/translation.service';
import { NavigationService } from '../../../Navigation/Services/navigation.service';
import { LogoutService } from '../../Services/logout.service';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { SETTINGS_PERMISSIONS } from '../../../../Enums/permission.enum';
import { SessionStorageService } from '../../Services/session-storage.service';
import { filter, map, startWith } from 'rxjs/operators';

@Component({
  selector: 'av-header',
  standalone: true,
  imports: [COMMON_IMPORTS, AvLanguageDropdownComponent, AvAvatarComponent],
  templateUrl: './av-header.component.html',
  styleUrls: ['./av-header.component.scss'],
})
export class AvHeaderComponent implements OnInit {
  translationService = inject(TranslationService);
  navigationService = inject(NavigationService);
  private router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _authService = inject(AuthService);
  private _logoutService = inject(LogoutService);
  private _sessionStorage = inject(SessionStorageService);

  private readonly RETURN_URL_KEY = 'settings_return_url';

  currentUser$ = this._authService.currentUser$;
  copyFeedbackTooltip = signal<string | null>(null);

  readonly ParentRoute = computed(() => this.router.url.split('/')[1]);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map((e: NavigationEnd) => e.urlAfterRedirects),
      startWith(this.router.url)
    )
  );

  readonly settingsTitleKey = computed<string | null>(() => {
    const url = this.currentUrl() ?? this.router.url;
    if (url.split('/')[1]?.split('?')[0] !== 'settings') return null;
    return this._readDeepestTitleKey() ?? 'shared.routes.app.settings';
  });

  private _readDeepestTitleKey(): string | null {
    let snapshot: ActivatedRouteSnapshot | null = this._route.snapshot;
    let key: string | null = null;
    while (snapshot) {
      key = snapshot.title ?? (snapshot.data['title'] as string | undefined) ?? key;
      snapshot = snapshot.firstChild;
    }
    return key;
  }

  private readonly _currentUser = toSignal(this._authService.currentUser$, { initialValue: null });

  readonly hasSettingsAccess = computed(() => {
    return this._currentUser()?.hasPermission(SETTINGS_PERMISSIONS) ?? false;
  });

  ngOnInit(): void {
    this.trackReturnUrl();
  }

  private trackReturnUrl(): void {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        if (!e.urlAfterRedirects.startsWith('/settings')) {
          this._sessionStorage.setItem(this.RETURN_URL_KEY, e.urlAfterRedirects);
        }
      });
  }

  getBackFromSettingsUrl(): string {
    const returnUrl = this._sessionStorage.getItem(this.RETURN_URL_KEY);
    return (returnUrl ?? '/home');
  }

  confirmLogout(): void {
    this._logoutService.confirmLogout();
  }



  copyToClipboard(text: string | undefined, tooltip: any): void {
    if (text) {
      navigator.clipboard.writeText(text).then(() => {
        this.copyFeedbackTooltip.set(this.translationService.instant('shared.general.tooltips.copy_email_tooltip.copied'));
        tooltip.show();

        setTimeout(() => {
          this.copyFeedbackTooltip.set(null);
        }, 5000);
      }).catch(err => {
        console.error('Failed to copy: ', err);
      });
    }
  }
}